"use client"

import Markdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { cn } from "@/lib/utils"
import { TypingIndicator } from "./typing-indicator"
import { useRelativeTime, type RelativeTimeLabels } from "./relative-time"
import { ArrowUpRightIcon } from "@/components/ui/icons"
import { AgentAvatar } from "./agent-avatar"
import type { ChatSource, ChatAttachment } from "./use-chat-session"
import { MarkerBreakdown, type MarkerBreakdownData } from "./marker-breakdown"
import { PricingBreakdown, type PricingCardWire } from "./pricing-breakdown"
import { Bubble, BubbleContent } from "./primitives/bubble"
import { Marker, MarkerContent } from "./primitives/marker"
import {
  Attachment,
  AttachmentContent,
  AttachmentDescription,
  AttachmentGroup,
  AttachmentMedia,
  AttachmentTitle,
} from "./primitives/attachment"

export type ChatMessageRole = "user" | "bot" | "human" | "system"

export interface ChatMessageProps {
  role: ChatMessageRole
  content: string
  isStreaming?: boolean
  agentName?: string
  /**
   * A human agent's Chatwoot profile photo, rendered as the avatar beside their
   * reply (Flavor A: left gutter, once per group, on the same last turn the
   * attribution line appears on). Only meaningful on `human` turns; Ella and the
   * visitor never get one. Empty/absent → initials fallback (see AgentAvatar).
   */
  agentAvatarUrl?: string
  timestamp?: string
  isCrisis?: boolean
  /**
   * "bubbled" (Chatwoot panel) wraps every message in a Bubble — the
   * standard chat pattern where AI + human replies share one visual language.
   * "bare" (inline Ella) drops the bubble for the assistant so the answer reads
   * as text directly on the card; the visitor's own message still gets a bubble.
   */
  variant?: "bubbled" | "bare"
  /**
   * When true, shows the sender attribution line ("AI assistant • Just now")
   * below the bubble. Callers set this only on the last turn of a group of
   * consecutive messages from the same sender.
   */
  showAttribution?: boolean
  /**
   * Translations injected from the parent so this component stays
   * i18n-agnostic. Defaults are fine for tests / standalone usage.
   */
  timeLabels?: RelativeTimeLabels
  botRoleLabel?: string
  humanRoleLabel?: string
  thinkingLabel?: string
  /**
   * Knowledge-base pages the bot cited for this turn. Rendered inside the bot
   * bubble, behind a hairline under the answer, when non-empty (AHD-9428).
   */
  sources?: ChatSource[]
  sourcesLabel?: string
  /**
   * Deterministic biomarker-list breakdown, rendered as a card beneath the bot
   * prose (the hybrid reply). Only present on bot turns that resolved a marker
   * list. Rendered from structured data, so it can't hallucinate a status.
   */
  markerBreakdown?: MarkerBreakdownData
  /** Deterministic package/price card (`pricing_card`, AHD-11297). glue emits
   *  the authoritative package prices; the widget builds the full card. */
  pricingCard?: PricingCardWire
  /** One-time "not medical advice" notice beneath a bot bubble (AHD-8753). */
  medicalNotice?: boolean
  medicalNoticeLabel?: string
  /** Files/images on this turn (images above the assistant text, below the
   *  visitor's). Render path is live; data path is dormant (AHD-7851). */
  attachments?: ChatAttachment[]
  /**
   * Renders a one-click language-switch offer on a system turn. `label` is the
   * already-translated sentence; `actionLabel` the button text. Absent on every
   * other turn.
   */
  localeSwitch?: { actionLabel: string; onSwitch: () => void }
}

const DEFAULT_TIME_LABELS: RelativeTimeLabels = {
  justNow: "Just now",
  minutesAgo: "{n} min ago",
  hoursAgo: "{n} h ago",
}

const CRISIS_PHONE_REGEX = /\b(112|113|143|144|147)\b/g

function renderCrisisContent(text: string): React.ReactNode {
  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null

  const re = new RegExp(CRISIS_PHONE_REGEX.source, "g")
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }
    const num = match[1]
    parts.push(
      <a
        key={match.index}
        href={`tel:${num}`}
        className="font-semibold underline"
      >
        {num}
      </a>
    )
    lastIndex = re.lastIndex
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }
  return parts
}

const MARKDOWN_COMPONENTS = {
  // Keep rendered markdown compact inside chat bubbles.
  p: ({ children }: { children?: React.ReactNode }) => (
    <p className="mb-2 last:mb-0">{children}</p>
  ),
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="mb-2 list-disc pl-4 last:mb-0">{children}</ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol className="mb-2 list-decimal pl-4 last:mb-0">{children}</ol>
  ),
  li: ({ children }: { children?: React.ReactNode }) => (
    <li className="mb-0.5">{children}</li>
  ),
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong className="font-semibold">{children}</strong>
  ),
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="underline"
    >
      {children}
    </a>
  ),
  h1: ({ children }: { children?: React.ReactNode }) => (
    <p className="mb-1 font-semibold">{children}</p>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <p className="mb-1 font-semibold">{children}</p>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <p className="mb-1 font-medium">{children}</p>
  ),
  // Don't render code blocks — bot shouldn't be emitting code.
  code: ({ children }: { children?: React.ReactNode }) => (
    <span>{children}</span>
  ),
  pre: ({ children }: { children?: React.ReactNode }) => (
    <span>{children}</span>
  ),
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function FileIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </svg>
  )
}

function Attachments({ items }: { items: ChatAttachment[] }) {
  return (
    <AttachmentGroup>
      {items.map((a) =>
        a.kind === "image" ? (
          <Attachment key={a.id} orientation="vertical">
            <AttachmentMedia variant="image">
              {/* eslint-disable-next-line @next/next/no-img-element -- runtime attachment URL, not a static asset */}
              <img src={a.url} alt={a.name ?? ""} />
            </AttachmentMedia>
          </Attachment>
        ) : (
          <Attachment key={a.id} orientation="horizontal">
            <AttachmentMedia variant="icon">
              <FileIcon />
            </AttachmentMedia>
            <AttachmentContent>
              <AttachmentTitle>{a.name ?? a.url}</AttachmentTitle>
              {a.sizeBytes != null && (
                <AttachmentDescription>
                  {formatBytes(a.sizeBytes)}
                </AttachmentDescription>
              )}
            </AttachmentContent>
          </Attachment>
        )
      )}
    </AttachmentGroup>
  )
}

function Attribution({
  name,
  roleLabel,
  timestamp,
  timeLabels,
}: {
  name: string
  roleLabel: string
  timestamp?: string
  timeLabels: RelativeTimeLabels
}) {
  const relative = useRelativeTime(timestamp ?? "", timeLabels)
  const parts = [name, roleLabel, relative].filter(Boolean)
  return (
    <span className="type-label-sm mt-1 px-1 text-neutral-500 normal-case">
      {parts.join(" • ")}
    </span>
  )
}

export function ChatMessage({
  role,
  content,
  isStreaming,
  agentName,
  agentAvatarUrl,
  timestamp,
  isCrisis,
  variant = "bubbled",
  showAttribution,
  timeLabels = DEFAULT_TIME_LABELS,
  botRoleLabel = "AI agent",
  humanRoleLabel = "Team",
  thinkingLabel = "Thinking…",
  sources,
  sourcesLabel = "Sources",
  markerBreakdown,
  pricingCard,
  medicalNotice,
  medicalNoticeLabel,
  attachments,
  localeSwitch,
}: ChatMessageProps) {
  // System notes render as a centered separator marker (date breaks, "connecting
  // you to the team", out-of-hours confirmations).
  if (role === "system") {
    return (
      <Marker variant="separator" data-chat-role="system">
        <MarkerContent>
          {content}
          {localeSwitch ? (
            <>
              {" "}
              <button
                type="button"
                onClick={localeSwitch.onSwitch}
                data-chat-action="locale-switch"
                className="text-foreground hover:text-deep-blue-950 focus-visible:ring-ring cursor-pointer underline underline-offset-3 focus-visible:ring-2 focus-visible:outline-none"
              >
                {localeSwitch.actionLabel}
              </button>
            </>
          ) : null}
        </MarkerContent>
      </Marker>
    )
  }

  const isUser = role === "user"
  const bare = variant === "bare" && !isUser
  const resolvedName = role === "human" ? (agentName ?? "") : ""
  const resolvedLabel =
    role === "bot" ? botRoleLabel : role === "human" ? humanRoleLabel : ""

  // The message body — crisis-aware plain text, markdown, or the streaming
  // typing indicator. Identical across bubbled / bare so styling never diverges.
  const body: React.ReactNode =
    isCrisis && content ? (
      <p className="mb-0">{renderCrisisContent(content)}</p>
    ) : isUser && content ? (
      // The visitor's own text is never markdown — it's whatever they typed, so
      // `# ` stays a hash and, now that the composer takes shift+enter, their
      // line breaks survive instead of collapsing into one run-on paragraph.
      <p className="mb-0 whitespace-pre-wrap">{content}</p>
    ) : content ? (
      <Markdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
        {content}
      </Markdown>
    ) : isStreaming ? (
      <TypingIndicator
        mode={role === "bot" ? "thinking" : "dots"}
        label={thinkingLabel}
      />
    ) : null

  // The bubble (or, when bare, the un-wrapped assistant text). 15px sits between
  // body-md (14) and body-lg (16) — no exact DS token, hence the ignore.
  const main = bare ? (
    <div
      data-chat-role={role}
      className={cn(
        "text-foreground max-w-full text-[15px] leading-relaxed" // ds-typo-ignore — chat copy sits between body-md (14) and body-lg (16)
      )}
    >
      {body}
    </div>
  ) : (
    // `default` paints the primary fill (navy) for the visitor; `muted` is the
    // neutral-50 incoming surface. Letting the primitive's own variants do this
    // is why neither side needs an `!important` override any more.
    <Bubble
      variant={isUser ? "default" : "muted"}
      align={isUser ? "end" : "start"}
    >
      <BubbleContent
        data-chat-role={role}
        className={cn(
          "rounded-2xl px-4 py-2 text-[15px] leading-relaxed", // ds-typo-ignore — chat bubble copy sits between body-md and body-lg
          isUser
            ? "text-primary-foreground"
            : "text-foreground border-transparent"
        )}
      >
        {body}
        {/* Citations belong to the message body, so they live inside the bubble
            behind a hairline — the narrow-support-widget pattern. Research
            surfaces use inline numbered superscripts instead, which a 2–4
            sentence answer off one or two KB pages doesn't warrant. */}
        {role === "bot" && sources && sources.length > 0 && (
          <div className="border-border/70 -mx-4 mt-2.5 border-t px-4 pt-2">
            <span className="type-label-sm block tracking-wider text-neutral-400">
              {sourcesLabel}
            </span>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {sources.map((s, i) =>
                s.url ? (
                  <a
                    key={i}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="type-label-sm text-deep-blue-800 inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 normal-case transition-colors hover:bg-white/70"
                  >
                    {s.label}
                    <ArrowUpRightIcon size={11} className="opacity-75" />
                  </a>
                ) : (
                  <span
                    key={i}
                    className="type-label-sm inline-flex items-center rounded-full bg-white px-2.5 py-1 text-neutral-600 normal-case"
                  >
                    {s.label}
                  </span>
                )
              )}
            </div>
          </div>
        )}
      </BubbleContent>
    </Bubble>
  )

  const hasAttachments = !!attachments && attachments.length > 0

  const column = (
    <div
      className={cn(
        "flex flex-col gap-0.5",
        isUser ? "items-end" : "items-start"
      )}
    >
      {/* Assistant attachments sit above the text; the visitor's sit below. */}
      {hasAttachments && !isUser && <Attachments items={attachments} />}
      {main}
      {/* The deterministic marker breakdown sits as its own full-width card
          beneath Ella's prose — a distinct data block, not bubble copy. */}
      {role === "bot" &&
        markerBreakdown &&
        markerBreakdown.markers.length > 0 && (
          <div className="mt-1.5 w-full">
            <MarkerBreakdown data={markerBreakdown} />
          </div>
        )}
      {/* Deterministic package/price card (AHD-11297) — same full-width slot,
          built from glue's authoritative prices + widget-owned presentation.
          Glue only emits a payload when there is a real card to show (the
          blood / membership variants carry no packages or add-ons of their own —
          the widget prices those — so a packages/addons length check would drop
          them, AHD-11637). Presence is the gate; glue guarantees meaningfulness. */}
      {role === "bot" && pricingCard && (
        <div className="mt-1.5 w-full">
          <PricingBreakdown wire={pricingCard} />
        </div>
      )}
      {hasAttachments && isUser && <Attachments items={attachments} />}

      {role === "bot" && medicalNotice && medicalNoticeLabel && (
        <div className="mt-1.5 px-1" data-chat-medical-notice>
          <span className="type-body-sm inline-flex items-center rounded-full bg-neutral-100 px-2.5 py-1 text-neutral-600">
            {medicalNoticeLabel}
          </span>
        </div>
      )}
      {showAttribution && !isUser && (resolvedName || resolvedLabel) && (
        <Attribution
          name={resolvedName}
          roleLabel={resolvedLabel}
          timestamp={timestamp}
          timeLabels={timeLabels}
        />
      )}
    </div>
  )

  // A human teammate's reply gets their Chatwoot photo in a left gutter. The
  // avatar sits in a row with the bubble (aligned to the bubble's bottom), and
  // the attribution drops beneath, indented under the bubble. It's shown once
  // per group — on the last turn — with the gutter reserved (a spacer) on the
  // earlier turns so a multi-message group stays left-aligned. Ella and the
  // visitor stay faceless, so a photo appearing is the signal a person took over.
  if (role === "human") {
    return (
      <div className="flex flex-col items-start gap-0.5">
        {/* The row and the column beside the avatar both take the full width on
            purpose. `Bubble` caps itself at `max-w-[80%]`, and a percentage
            resolves against its containing block — so a shrink-to-fit column
            would size itself to the message's own natural width and then clip
            the bubble to 80% of that, wrapping every reply (a bare "testing"
            broke as "testi/ng"). Full-width parents make the 80% mean the same
            thing here as it does on Ella's and the visitor's turns. */}
        <div className="flex w-full items-end gap-2">
          {showAttribution ? (
            <AgentAvatar avatarUrl={agentAvatarUrl} name={agentName} />
          ) : (
            <span className="w-7 shrink-0" aria-hidden="true" />
          )}
          <div className="flex min-w-0 flex-1 flex-col items-start gap-0.5">
            {hasAttachments && <Attachments items={attachments} />}
            {main}
          </div>
        </div>
        {/* Show the attribution once the name OR the role is known, so a rare
            nameless agent turn still reads as "Ahead team" rather than nothing. */}
        {showAttribution && (resolvedName || resolvedLabel) && (
          <div className="pl-9">
            <Attribution
              name={resolvedName}
              roleLabel={resolvedLabel}
              timestamp={timestamp}
              timeLabels={timeLabels}
            />
          </div>
        )}
      </div>
    )
  }

  return column
}
